import { Api } from '@firmable/contracts';
import { useQuery } from '@tanstack/react-query';
import { Query } from '@web/constants/query';
import * as Supabase from '@web/contracts/Supabase';
import { hasSubscription } from '@web/guards/Subscription';
import { App } from '@web/query/App';
import axios from 'axios';
import React from 'react';

export interface ApiResponse {
  plan: Api.Supabase.Plans.AddonCreditPlan;
  success: true;
}

export interface Failure {
  message: string;
}

/**
 * React Query hook to fetch addon credit price for given subscription plan id
 * @returns ApiResponse | undefined
 */
const useAddonCreditPlans = () => {
  const { data: subscriptionData, isFetching } = App.useSubscriptions();

  const subscription = React.useMemo(() => {
    if (hasSubscription(subscriptionData)) {
      const subscriptionId = subscriptionData.subscription.plan.product as string;
      const billingCountry = subscriptionData.subscription.billingCountry;
      return { subscriptionId, billingCountry };
    }
  }, [subscriptionData]);

  return useQuery<Supabase.Plans.AddonCreditPlan, Failure>({
    queryKey: [Query.SupabaseKeys['addon-credit-plan']],
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Infinity,
    enabled: !isFetching && !!subscription,
    queryFn: async () => {
      const response = await axios.get<ApiResponse>(
        `${process.env.NEXT_PUBLIC_API_APP_ENDPOINT}/plans/${subscription!.subscriptionId}/addon-credits`,
        {
          params: {
            billingCountry: subscription!.billingCountry,
          },
        },
      );
      return response.data.plan;
    },
  });
};

export { useAddonCreditPlans };
